import React, { useState, useEffect } from "react";
import { Card, Row, Col, Badge, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import CountUp from "react-countup";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { jwtDecode } from "jwt-decode";
import {
  Users,
  Megaphone,
  AlertTriangle,
  Clock,
  XCircle,
  ArrowRightLeft,
  FileText,
  TrendingUp,
  Calendar,
} from "lucide-react";
import "../../styles/Dashboard.css";

ChartJS.register(ArcElement, Tooltip, Legend);

const Dashboard = () => {
  const navigate = useNavigate();
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [promotionsCount, setPromotionsCount] = useState(0);
  const [complaintsSummary, setComplaintsSummary] = useState({});
  const [requestsSummary, setRequestsSummary] = useState({});
  const [userRole, setUserRole] = useState(null);
  const API_URL = process.env.REACT_APP_API_URL;

  const authToken = localStorage.getItem("authToken");

  // Get user role from token
  useEffect(() => {
    try {
      if (authToken) {
        const decoded = jwtDecode(authToken);
        const role = decoded.role ? decoded.role.toLowerCase() : null;
        setUserRole(role);
      }
    } catch (err) {
      console.error("Error decoding token:", err);
    }
  }, [authToken]);

  useEffect(() => {
    // Only fetch data for routes the user has access to
    if (userRole === "admin" || userRole === "manager") {
      fetchCustomers();
    }
    if (userRole === "admin" || userRole === "branding") {
      fetchPromotions();
    }
    if (userRole === "admin" || userRole === "csd") {
      fetchComplaints();
      fetchRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole]);

  const axiosConfig = {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/customers`, axiosConfig);
      const uniqueCount = new Set(
        response.data.map((c) => c.CusID).filter(Boolean),
      ).size;
      setTotalCustomers(uniqueCount);
    } catch (error) {
      // Silently handle 403 - user doesn't have access (expected for CSD, Branding)
      if (error.response?.status !== 403) {
        console.error("Error fetching customers:", error);
      }
    }
  };

  const fetchPromotions = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/promotions`,
        axiosConfig,
      );
      setPromotionsCount(response.data.length);
    } catch (error) {
      // Silently handle 403 - user doesn't have access (expected for CSD, Manager)
      if (error.response?.status !== 403) {
        console.error("Error fetching promotions:", error);
      }
    }
  };

  const fetchComplaints = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/complaints`,
        axiosConfig,
      );
      const summary = {
        Pending: response.data.filter((c) => c.Status === "Pending").length,
        Resolved: response.data.filter((c) => c.Status === "Resolved").length,
        "In Progress": response.data.filter((c) => c.Status === "In Progress")
          .length,
        Rejected: response.data.filter((c) => c.Status === "Rejected").length,
      };
      setComplaintsSummary(summary);
    } catch (error) {
      console.error("Error fetching complaints:", error);
    }
  };

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/requests`, axiosConfig);
      const summary = {
        Recorded: response.data.filter((r) => r.ReqStatusDesc === "Pending")
          .length,
        Completed: response.data.filter((r) => r.ReqStatusDesc === "Completed")
          .length,
        Rejected: response.data.filter((r) => r.ReqStatusDesc === "Rejected")
          .length,
        "Forwarded to Marketing": response.data.filter(
          (r) => r.ReqStatusDesc === "Forwarded To Marketing",
        ).length,
        "Forwarded to Operations": response.data.filter(
          (r) => r.ReqStatusDesc === "Forwarded To Operation",
        ).length,
      };
      setRequestsSummary(summary);
    } catch (error) {
      console.error("Error fetching requests:", error);
    }
  };

  const totalComplaints =
    (complaintsSummary.Pending || 0) +
    (complaintsSummary.Resolved || 0) +
    (complaintsSummary["In Progress"] || 0) +
    (complaintsSummary.Rejected || 0);

  const totalRequests =
    (requestsSummary.Recorded || 0) +
    (requestsSummary.Completed || 0) +
    (requestsSummary.Rejected || 0) +
    (requestsSummary["Forwarded to Marketing"] || 0) +
    (requestsSummary["Forwarded to Operations"] || 0);

  const complaintsChartData = {
    labels: ["Pending", "Resolved", "In Progress", "Rejected"],
    datasets: [
      {
        data: [
          complaintsSummary.Pending || 0,
          complaintsSummary.Resolved || 0,
          complaintsSummary["In Progress"] || 0,
          complaintsSummary.Rejected || 0,
        ],
        backgroundColor: ["#f97316", "#22c55e", "#3b82f6", "#ef4444"],
        borderWidth: 0,
        cutout: "70%",
      },
    ],
  };

  const requestsChartData = {
    labels: [
      "Recorded",
      "Completed",
      "Rejected",
      "Fwd Marketing",
      "Fwd Operations",
    ],
    datasets: [
      {
        data: [
          requestsSummary.Recorded || 0,
          requestsSummary.Completed || 0,
          requestsSummary.Rejected || 0,
          requestsSummary["Forwarded to Marketing"] || 0,
          requestsSummary["Forwarded to Operations"] || 0,
        ],
        backgroundColor: [
          "#3b82f6",
          "#22c55e",
          "#ef4444",
          "#8b5cf6",
          "#06b6d4",
        ],
        borderWidth: 0,
        cutout: "70%",
      },
    ],
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          padding: 16,
          usePointStyle: true,
          pointStyleWidth: 10,
          font: { size: 12 },
          color: "#475569",
        },
      },
      tooltip: {
        backgroundColor: "#1e293b",
        padding: 10,
        cornerRadius: 8,
      },
    },
  };

  // Stat card helper
  const StatCard = ({
    icon: Icon,
    iconColor,
    iconBg,
    label,
    value,
    subtitle,
    onClick,
  }) => (
    <Card
      className="dash-stat-card h-100 border-0"
      onClick={onClick}
      style={{ cursor: onClick ? "pointer" : "default" }}
    >
      <Card.Body className="d-flex align-items-center gap-3">
        <div className="dash-icon-wrap" style={{ backgroundColor: iconBg }}>
          <Icon size={22} color={iconColor} />
        </div>
        <div className="flex-grow-1">
          <div className="text-muted small fw-medium">{label}</div>
          <div className="dash-stat-value">
            <CountUp end={value} duration={1.8} separator="," />
          </div>
          {subtitle && (
            <div className="text-muted" style={{ fontSize: "0.75rem" }}>
              {subtitle}
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );

  if (!userRole) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "60vh" }}
      >
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="dash-container">
      {/* Header */}
      <div className="dash-header mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-1">
            <TrendingUp size={28} className="me-2 text-primary" />
            Dashboard
          </h2>
          <p className="text-muted mb-0 small d-flex align-items-center gap-1">
            <Calendar size={14} />
            {new Date().toLocaleDateString("en-US", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        </div>
        <Badge bg="light" text="dark" className="dash-role-badge">
          {userRole?.toUpperCase()}
        </Badge>
      </div>

      {/* ══════════ Overview ══════════ */}
      {(userRole === "admin" ||
        userRole === "manager" ||
        userRole === "branding") && (
        <>
          <h6 className="dash-section-label">Overview</h6>
          <Row className="g-3 mb-4">
            {(userRole === "admin" || userRole === "manager") && (
              <Col xl={3} lg={6} sm={6}>
                <StatCard
                  icon={Users}
                  iconColor="#3b82f6"
                  iconBg="#eff6ff"
                  label="Total Customers"
                  value={totalCustomers}
                  onClick={() => navigate("/customers")}
                />
              </Col>
            )}
            {(userRole === "admin" || userRole === "branding") && (
              <Col xl={3} lg={6} sm={6}>
                <StatCard
                  icon={Megaphone}
                  iconColor="#22c55e"
                  iconBg="#f0fdf4"
                  label="Total Promotions"
                  value={promotionsCount}
                  onClick={() => navigate("/promotions")}
                />
              </Col>
            )}
          </Row>
        </>
      )}

      {/* ══════════ Complaints ══════════ */}
      {(userRole === "admin" || userRole === "csd") && (
        <>
          <h6 className="dash-section-label">Complaints</h6>
          <Row className="g-3 mb-4">
            <Col xl={3} lg={6} sm={6}>
              <StatCard
                icon={AlertTriangle}
                iconColor="#f97316"
                iconBg="#fff7ed"
                label="Pending Complaints"
                value={complaintsSummary.Pending || 0}
                subtitle={`of ${totalComplaints} total`}
                onClick={() => navigate("/assistances")}
              />
            </Col>
            <Col xl={3} lg={6} sm={6}>
              <StatCard
                icon={Clock}
                iconColor="#8b5cf6"
                iconBg="#f5f3ff"
                label="In Progress"
                value={complaintsSummary["In Progress"] || 0}
                subtitle="complaints being handled"
                onClick={() => navigate("/assistances")}
              />
            </Col>
          </Row>
        </>
      )}

      {/* ══════════ Requests ══════════ */}
      {(userRole === "admin" || userRole === "csd") && (
        <>
          <h6 className="dash-section-label">Requests</h6>
          <Row className="g-3 mb-4">
            <Col xl={3} lg={6} sm={6}>
              <StatCard
                icon={FileText}
                iconColor="#3b82f6"
                iconBg="#eff6ff"
                label="New Requests"
                value={requestsSummary.Recorded || 0}
                subtitle={`of ${totalRequests} total`}
                onClick={() => navigate("/requests")}
              />
            </Col>
            <Col xl={3} lg={6} sm={6}>
              <StatCard
                icon={ArrowRightLeft}
                iconColor="#8b5cf6"
                iconBg="#f5f3ff"
                label="Fwd to Marketing"
                value={requestsSummary["Forwarded to Marketing"] || 0}
                onClick={() => navigate("/requests")}
              />
            </Col>
            <Col xl={3} lg={6} sm={6}>
              <StatCard
                icon={XCircle}
                iconColor="#06b6d4"
                iconBg="#ecfeff"
                label="Fwd to Operations"
                value={requestsSummary["Forwarded to Operations"] || 0}
                onClick={() => navigate("/requests")}
              />
            </Col>
          </Row>
        </>
      )}

      {/* ══════════ Charts Section ══════════ */}
      {(userRole === "admin" || userRole === "csd") && (
        <>
          {/* Doughnut Charts + Progress Breakdown */}
          <Row className="g-3 mb-4">
            <Col lg={6}>
              <Card className="dash-chart-card border-0 h-100">
                <Card.Body>
                  <Card.Title className="fw-semibold mb-3">
                    Complaints Breakdown
                  </Card.Title>
                  <div style={{ height: "240px" }}>
                    <Doughnut
                      data={complaintsChartData}
                      options={doughnutOptions}
                    />
                  </div>
                  <div className="text-center mt-2">
                    <span className="h3 fw-bold text-dark">
                      {totalComplaints}
                    </span>
                    <br />
                    <span className="text-muted small">Total Complaints</span>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={6}>
              <Card className="dash-chart-card border-0 h-100">
                <Card.Body>
                  <Card.Title className="fw-semibold mb-3">
                    Requests Breakdown
                  </Card.Title>
                  <div style={{ height: "240px" }}>
                    <Doughnut
                      data={requestsChartData}
                      options={doughnutOptions}
                    />
                  </div>
                  <div className="text-center mt-2">
                    <span className="h3 fw-bold text-dark">
                      {totalRequests}
                    </span>
                    <br />
                    <span className="text-muted small">Total Requests</span>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default Dashboard;
